import { SearchRegistry } from '../types';

export const partnerAdministrationSearchEntries: SearchRegistry = {
  '/partner-administration/overview': {
    keywords: [
      'partner administration',
      'partner admin overview',
      'partner workspace',
      'partner operations',
      'partner setup',
      'partner management overview',
      'internal partner tools',
    ],
    questions: [
      'Where is the partner administration overview?',
      'Where do I manage partner setup and onboarding?',
    ],
  },
  '/partner-administration/list': {
    keywords: [
      'partner list',
      'all partners',
      'partner configurations',
      'partner config',
      'partner search',
      'find partner',
      'edit partner',
      'partner feature flags',
      'partner settings',
      'static egress allowlist',
      'ip allowlist',
    ],
    questions: [
      'Where do I find an existing partner?',
      'How do I open a partner configuration?',
      'Where can I review partner feature flags?',
    ],
  },
  '/partner-administration/create': {
    keywords: [
      'add partner',
      'create partner',
      'new partner',
      'partner record',
      'partner configuration',
      'internal partner name',
      'partner setup form',
    ],
    questions: ['How do I create a new partner?', 'Where do I add a partner configuration?'],
  },
  '/partner-administration/edit/:partnerConfigId': {
    keywords: [
      'edit partner',
      'update partner',
      'partner details',
      'partner configuration detail',
      'change partner settings',
      'partner config id',
      'static egress allowlist',
      'webhook ip allowlist',
    ],
    questions: [
      'Where do I update an existing partner?',
      'How do I change partner configuration details?',
    ],
  },
  '/partner-administration/tokens/:partnerConfigId': {
    keywords: [
      'partner tokens',
      'api token',
      'manage partner token',
      'environment token',
      'partner access token',
      'token configuration',
    ],
    questions: [
      'Where do I manage partner tokens?',
      'How do I update a partner token for an environment?',
    ],
  },
  '/partner-administration/api-credentials/:partnerConfigId': {
    keywords: [
      'api credentials',
      'partner api credentials',
      'client credentials',
      'internal api credentials',
      'partner secret',
      'credential management',
    ],
    questions: [
      'Where do I manage partner API credentials?',
      'How do I rotate or review partner credentials?',
    ],
  },
  '/partner-administration/partner-integration-kb': {
    keywords: [
      'partner integration kb',
      'partner integration knowledge base',
      'integration knowledge',
      'partner documentation',
      'partner implementation notes',
      'cooperation partner onboarding',
      'cooperation partner subfleet',
      'subfleet onboarding',
      'pairpoint onboarding',
      'pairpoint fleet onboarding',
      'aumovio onboarding',
      'aumovio fleet onboarding',
      'require2FAForFuelCardCheckout',
      'externalIds companyId driverId',
      'oem partner onboarding',
      'oem onboarding',
      'fuelCardWalletForPartnerOEM',
      'PARTNER_BACKEND user type',
    ],
    questions: [
      'Where is the partner integration knowledge base?',
      'Where can I find internal notes for partner integrations?',
    ],
  },
  '/partner-administration/ui-settings/list': {
    keywords: [
      'partner ui settings',
      'ui settings list',
      'partner branding',
      'white label settings',
      'app ui settings',
      'partner frontend settings',
    ],
    questions: [
      'Where do I find partner UI settings?',
      'How do I review existing UI settings for a partner?',
    ],
  },
  '/partner-administration/ui-settings/create': {
    keywords: [
      'create ui settings',
      'add ui settings',
      'partner branding create',
      'new partner ui settings',
      'white label setup',
    ],
    questions: [
      'Where do I create UI settings for a partner?',
      'How do I set up partner branding?',
    ],
  },
  '/partner-administration/ui-settings/edit': {
    keywords: [
      'edit ui settings',
      'update ui settings',
      'change partner branding',
      'update white label settings',
      'partner frontend configuration',
    ],
    questions: [
      'Where do I update partner UI settings?',
      'How do I change partner branding settings?',
    ],
  },
  '/partner-administration/key-account-managers': {
    keywords: [
      'key account managers',
      'kam',
      'partner account owner',
      'account manager assignment',
      'partner owner',
      'business development contact',
    ],
    questions: [
      'Where do I manage key account managers?',
      'How do I assign a key account manager to a partner?',
    ],
  },
  '/partner-administration/alert-slack-channels': {
    keywords: [
      'partner alert slack channels',
      'alert slack channels',
      'slack alerts',
      'partner notifications',
      'slack webhook alerts',
      'alert channel routing',
    ],
    questions: [
      'Where do I manage Slack channels for partner alerts?',
      'How do I add a partner alert Slack channel?',
    ],
  },
  '/partner-administration/partner-onboarding': {
    keywords: [
      'partner onboarding',
      'onboard new partner',
      'new partner wizard',
      'guided onboarding',
      'partner handoff',
      'api partner onboarding',
      'fleet api onboarding',
      'energy network onboarding',
      'oem partner onboarding',
      'car manufacturer onboarding',
    ],
    questions: [
      'Where do I start onboarding a new partner?',
      'Which onboarding flow should I use for a partner?',
      'How do I onboard an OEM partner?',
    ],
    faqs: [
      {
        q: 'Which page should I use to onboard a new partner?',
        a: 'Open Partner Administration > Partner Onboarding. Choose the flow that matches the partner type, such as rydpay API partner, OEM partner (car manufacturer), Fleet API partner, energy network partner, or manual handoff.',
      },
      {
        q: 'How do I onboard an OEM partner such as Mercedes or Škoda?',
        a: 'Choose OEM partner (car manufacturer) on the Partner Onboarding page. It runs the rydpay API partner journey, which creates the API account with user type PARTNER_BACKEND. OEMs using the hosted fuel-card wallet with PIN validation additionally need the fuelCardWalletForPartnerOEM feature configuration on the sponsor.',
      },
    ],
  },
  '/partner-administration/partner-onboarding/api-partner': {
    keywords: [
      'rydpay api partner onboarding',
      'api partner onboarding',
      'payment api partner',
      'virtual account token',
      'api-only partner setup',
      'partner config setup',
    ],
    questions: [
      'How do I onboard a rydpay API partner?',
      'Which steps create an API-only partner setup?',
    ],
  },
  '/partner-administration/partner-onboarding/fleet-apis': {
    keywords: [
      'fleet api partner onboarding',
      'fleet apis onboarding',
      'fleet partner api setup',
      'fleet integration onboarding',
      'fleet sponsor setup',
      'fleet partner handoff',
    ],
    questions: [
      'How do I onboard a Fleet API partner?',
      'Which steps are needed for a fleet API integration?',
    ],
  },
  '/partner-administration/partner-onboarding/energy-network': {
    keywords: [
      'energy network onboarding',
      'charging partner onboarding',
      'ev charging partner',
      'energy partner setup',
      'charge point partner',
    ],
    questions: [
      'How do I onboard an energy network partner?',
      'Which onboarding flow is used for charging partners?',
    ],
  },
  '/partner-administration/partner-onboarding/manual-handoff/:flowType': {
    keywords: [
      'manual handoff',
      'manual onboarding handoff',
      'partner onboarding checklist',
      'handoff flow',
      'business development handoff',
      'key account manager handoff',
    ],
    questions: [
      'Where do I document a manual onboarding handoff?',
      'How do I continue onboarding when the automated flow is not enough?',
    ],
  },
  '/partner-administration/partner-onboarding/journeys': {
    keywords: [
      'onboarding journeys',
      'partner onboarding history',
      'onboarding audit',
      'journey list',
      'onboarding status',
      'completed onboarding steps',
      'failed onboarding steps',
    ],
    questions: [
      'Where do I see partner onboarding journeys?',
      'How do I search onboarding history by partner or status?',
    ],
  },
  '/partner-administration/partner-onboarding/journeys/:journeyId': {
    keywords: [
      'onboarding journey detail',
      'journey timeline',
      'onboarding events',
      'onboarding step details',
      'raw onboarding metadata',
      'failed onboarding event',
    ],
    questions: [
      'Where do I inspect one onboarding journey?',
      'How do I see which onboarding step failed?',
    ],
  },
  '/partner-administration/partner-onboarding/checklists': {
    keywords: [
      'partner checklists',
      'onboarding checklist',
      'kam checklist',
      'key account manager checklist',
      'rydpay checklist',
      'fleet apis checklist',
    ],
    questions: [
      'Where do I see all partner onboarding checklists?',
      'How do I create or open a partner checklist?',
    ],
  },
};
